import { FC, useContext } from 'react';

import { useQuery } from '@apollo/client';
import { InfoBlock, Preloader } from '@smartshell/ui-kit';

import { AuthContext, PermissionHOC } from 'auth';
import {
  Deposit,
  MoneyByType,
  MovementMoney,
  Proceeds,
  WorkerBlock,
} from 'components';
import { FeatureHOC } from 'features';
import { useWindowResize } from 'hooks';
import { LocalizationContext } from 'localization';
import {
  GET_WORKSHIFT_MONEY,
  IGetWorkShiftMoneyRequest,
  IGetWorkShiftMoneyResponse,
} from 'schemas';

import { ActiveClientsBlock } from './ActiveClientsBlock';
import s from './Dashboard.module.scss';
import { GameAccountsBlock } from './GameAccountsBlock';
import { HostStatusesBlock } from './HostStatuses';
import { SoldItemsBlock } from './SoldItemsBlock';
import { TasksBlock } from './TasksBlock';

export const Dashboard: FC = () => {
  const {
    data: { permissions },
  } = useContext(AuthContext);

  const size = useWindowResize();

  const { t } = useContext(LocalizationContext);

  const { data: workshiftMoneyData, loading } = useQuery<
    IGetWorkShiftMoneyResponse,
    IGetWorkShiftMoneyRequest
  >(GET_WORKSHIFT_MONEY, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 30000,
    errorPolicy: 'ignore',
    skip: !permissions?.has('work_shift_active'),
  });

  if (loading && !workshiftMoneyData) {
    return <Preloader loading />;
  }

  return (
    <div>
      <div className={s.top}>
        <PermissionHOC
          permission="work_shift_active"
          className={s.workShiftActiveNotPermission}
        >
          <WorkerBlock isLoading={loading} />
          <Proceeds />
        </PermissionHOC>
        <PermissionHOC
          permission="work_shift_active"
          className={s.workShiftActiveNotPermission}
        >
          <MoneyByType />
          <MovementMoney />
          <Deposit />
        </PermissionHOC>
      </div>

      <div className={s.container}>
        <PermissionHOC
          permission="host_browse"
          className={s.hostStatusNotPermission}
        >
          <HostStatusesBlock />
        </PermissionHOC>

        {size?.width > 960 && (
          <>
            <PermissionHOC
              permission="worker_task_browse"
              className={s.notPermission}
            >
              <TasksBlock permissions={permissions} />
            </PermissionHOC>

            <PermissionHOC
              permission="work_shift_active"
              className={s.notPermission}
            >
              <SoldItemsBlock />
            </PermissionHOC>

            <FeatureHOC alias="activity_rating" strategy="preview">
              <PermissionHOC
                permission="client_info"
                className={s.notPermission}
              >
                <ActiveClientsBlock />
              </PermissionHOC>
            </FeatureHOC>

            <FeatureHOC alias="game_accounts_widget" strategy="preview">
              <PermissionHOC
                permission="game_account_browse"
                className={s.notPermission}
              >
                <GameAccountsBlock />
              </PermissionHOC>
            </FeatureHOC>
          </>
        )}
      </div>

      <InfoBlock
        className={s.mobile_info}
        title={t('dashboard.info.mobile')}
        iconName="ComputerIcon"
        color="blue"
      />
    </div>
  );
};
